import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import MLBGameCard from './MLBGameCard';
const MLBConfidenceTiers = ({ games }) => {
    // Helper function to determine effective confidence level based on professional betting decision
    const getEffectiveConfidenceLevel = (game) => {
        if (game.professional_bet_decision?.should_bet === true) {
            return game.recommended_bet?.confidence_level || 'MEDIUM';
        }
        // Check new structure
        if (game.betting_analysis?.best_bet_recommendation?.recommendation === 'PLACE_BET') {
            return game.betting_analysis.best_bet_recommendation.confidence_tier || 'MEDIUM';
        }
        return 'NO_BET';
    };
    // Helper function to calculate average metric for a tier
    const calculateTierAverage = (tierGames, metricGetter, fallback) => {
        if (tierGames.length === 0)
            return fallback;
        const sum = tierGames.reduce((acc, game) => acc + metricGetter(game), 0);
        return sum / tierGames.length;
    };
    // Helper function to get true model confidence for a game
    const getTrueModelConfidence = (game) => {
        // Check new structure first - use the probability from the best bet recommendation
        if (game.betting_analysis?.best_bet_recommendation?.bet_details?.probability) {
            return game.betting_analysis.best_bet_recommendation.bet_details.probability;
        }
        // Fallback to spread prediction confidence
        if (game.spread_prediction?.home_covers_runline_prob) {
            return Math.max(game.spread_prediction.home_covers_runline_prob, game.spread_prediction.away_covers_runline_prob || 0);
        }
        // Legacy compatibility
        if (game.legacy_compatibility?.prediction_confidence) {
            return game.legacy_compatibility.prediction_confidence;
        }
        // For games with explicit recommendations, use the probability directly
        if (game.recommended_bet?.team === 'HOME' || game.recommended_bet?.team === 'AWAY') {
            return game.recommended_bet.probability;
        }
        // For NO_BET games, calculate based on model prediction
        const ensemblePrediction = game.model_predictions?.ensemble?.prediction || 0;
        if (ensemblePrediction === 0) {
            // Model predicts home team covers
            return game.prediction_confidence || 0.5;
        }
        else {
            // Model predicts away team covers - invert the ensemble_probability
            return 1 - (game.ensemble_probability || 0.5);
        }
    };
    // Use the SAME logic as MLBGameCard to ensure consistency
    const getTrueConfidenceLevel = (game) => {
        // Use new confidence tier structure first
        const confidenceTier = game.betting_analysis?.best_bet_recommendation?.confidence_tier;
        if (confidenceTier === 'HIGH_CONFIDENCE_BET')
            return 'HIGH';
        if (confidenceTier === 'MODERATE_CONFIDENCE_BET')
            return 'MEDIUM_PLUS';
        if (confidenceTier === 'LOW_CONFIDENCE_BET')
            return 'LOW';
        // Use the actual confidence_level from the data file (same as MLBGameCard)
        const level = game.recommended_bet?.confidence_level;
        if (level === 'MEDIUM_PLUS') {
            return 'MEDIUM_PLUS';
        }
        return level || 'MEDIUM';
    };
    // Remove duplicates by creating a Set of unique game IDs we've seen
    const processedGameIds = new Set();
    // Filter games by TRUE confidence level - ensure no duplicates
    const highConfidenceGames = games.filter(g => {
        const gameId = g.game_info?.game_id || g.game_id || 0;
        if (processedGameIds.has(gameId))
            return false;
        if (getTrueConfidenceLevel(g) === 'HIGH') {
            processedGameIds.add(gameId);
            return true;
        }
        return false;
    });
    const mediumPlusGames = games.filter(g => {
        const gameId = g.game_info?.game_id || g.game_id || 0;
        if (processedGameIds.has(gameId))
            return false;
        if (getTrueConfidenceLevel(g) === 'MEDIUM_PLUS') {
            processedGameIds.add(gameId);
            return true;
        }
        return false;
    });
    const mediumGames = games.filter(g => {
        const gameId = g.game_info?.game_id || g.game_id || 0;
        if (processedGameIds.has(gameId))
            return false;
        if (getTrueConfidenceLevel(g) === 'MEDIUM') {
            processedGameIds.add(gameId);
            return true;
        }
        return false;
    });
    const lowGames = games.filter(g => {
        const gameId = g.game_info?.game_id || g.game_id || 0;
        if (processedGameIds.has(gameId))
            return false;
        if (getTrueConfidenceLevel(g) === 'LOW') {
            processedGameIds.add(gameId);
            return true;
        }
        return false;
    });
    const tiers = [
        {
            level: 'HIGH',
            label: 'High Confidence Picks',
            color: 'var(--bs-success)', // Green for high confidence
            games: highConfidenceGames,
            historical_accuracy: calculateTierAverage(highConfidenceGames, g => g.display_classification?.confidence_classification.validated_performance.accuracy || 75.0, 75.0),
            historical_roi: calculateTierAverage(highConfidenceGames, g => g.display_classification?.confidence_classification.validated_performance.roi || 12.0, 12.0)
        },
        {
            level: 'MEDIUM_PLUS',
            label: 'Medium-Plus Confidence Picks',
            color: 'var(--bs-primary)', // Blue for medium-plus
            games: mediumPlusGames,
            historical_accuracy: calculateTierAverage(mediumPlusGames, g => g.display_classification?.confidence_classification.validated_performance.accuracy || 68.0, 68.0),
            historical_roi: calculateTierAverage(mediumPlusGames, g => g.display_classification?.confidence_classification.validated_performance.roi || 8.5, 8.5)
        },
        {
            level: 'MEDIUM',
            label: 'Medium Confidence Picks',
            color: 'var(--bs-warning)', // Yellow for medium
            games: mediumGames,
            historical_accuracy: calculateTierAverage(mediumGames, g => g.display_classification?.confidence_classification.validated_performance.accuracy || 62.0, 62.0),
            historical_roi: calculateTierAverage(mediumGames, g => g.display_classification?.confidence_classification.validated_performance.roi || 5.0, 5.0)
        },
        {
            level: 'LOW',
            label: 'Low Confidence / Avoid',
            color: 'var(--bs-secondary)', // Gray for low/avoid
            games: lowGames,
            historical_accuracy: calculateTierAverage(lowGames, g => g.display_classification?.confidence_classification.validated_performance.accuracy || 50.0, 50.0),
            historical_roi: calculateTierAverage(lowGames, g => g.display_classification?.confidence_classification.validated_performance.roi || -1.0, -1.0)
        }
    ];
    return (_jsxs("div", { className: "confidence-tiers", children: [tiers.map((tier) => {
                if (tier.games.length === 0)
                    return null;
                return (_jsxs("div", { className: "tier-section mb-5", children: [_jsx("div", { className: "tier-header mb-4", children: _jsxs("div", { className: "d-flex justify-content-between align-items-center", children: [_jsxs("div", { children: [_jsxs("h3", { className: "mb-1 text-body-emphasis", style: { fontWeight: '600' }, children: [_jsx("i", { className: "fas fa-chart-bar me-2", style: { color: tier.color } }), tier.label] }), _jsxs("p", { className: "text-muted mb-0", children: [tier.games.length, " game", tier.games.length !== 1 ? 's' : '', " \u2022 Historical: ", (tier.historical_accuracy || 0).toFixed(1), "% accuracy, ", (tier.historical_roi || 0).toFixed(1), "% ROI"] })] }), _jsx("div", { className: "tier-badge", children: _jsx("span", { className: "badge bg-light text-dark fs-6 px-3 py-2", style: {
                                                border: `2px solid ${tier.color}`,
                                                color: tier.color,
                                                backgroundColor: 'var(--bs-body-bg)'
                                            }, children: tier.games.length }) })] }) }), _jsx("div", { className: "row g-4", children: tier.games
                                .sort((a, b) => {
                                // First sort by professional criteria (Pro picks first)
                                const aPro = a.meets_professional_criteria || false;
                                const bPro = b.meets_professional_criteria || false;
                                if (aPro !== bPro)
                                    return bPro ? 1 : -1;
                                // Then by probability (higher first)
                                const aProb = a.recommended_bet?.probability ||
                                    a.betting_analysis?.best_bet_recommendation?.bet_details?.probability ||
                                    0.5;
                                const bProb = b.recommended_bet?.probability ||
                                    b.betting_analysis?.best_bet_recommendation?.bet_details?.probability ||
                                    0.5;
                                return bProb - aProb;
                            })
                                .map((game) => (_jsx("div", { className: "col-lg-6 col-xl-4", children: _jsx(MLBGameCard, { game: game, tierColor: tier.color }) }, game.game_info?.game_id || game.game_id))) })] }, tier.level));
            }), games.length === 0 && (_jsxs("div", { className: "text-center py-5", children: [_jsx("i", { className: "fas fa-search fa-3x text-muted mb-3" }), _jsx("h4", { className: "text-muted", children: "No games found" }), _jsx("p", { className: "text-muted", children: "Try adjusting your filters to see more games." })] }))] }));
};
export default MLBConfidenceTiers;
